import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "@/api/adminOneClient";
import ComplianceLock from "@/components/compliance/ComplianceLock";

const SEVERITIES = ["LOW", "MEDIUM", "HIGH", "EXTREME"];
const SEV_COLOR = { LOW: "#7AC860", MEDIUM: "#F5C842", HIGH: "#E8A020", EXTREME: "#C84A3A" };
const STATUSES = ["OPEN", "MONITORING", "ESCALATED", "CLOSED"];
const WATCHTOWERS = ["markets", "healthcare", "macro", "geopolitics", "commodities"];

const TABS = [
  { key: "active", label: "Active Signals" },
  { key: "heatmap", label: "Heatmap" },
  { key: "timeline", label: "Timeline" },
  { key: "lifecycle", label: "Lifecycle" },
  { key: "correlations", label: "Correlations" },
  { key: "escalations", label: "Escalations" },
  { key: "watchlists", label: "Watchlists" },
];

export default function AdminSignals() {
  const [tab, setTab] = useState("active");
  const [signals, setSignals] = useState([]);
  const [filters, setFilters] = useState({ watchtower: "", severity: "", status: "" });
  const [heatmap, setHeatmap] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [lifecycle, setLifecycle] = useState({});
  const [correlations, setCorrelations] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [scoresTop, setScoresTop] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadActive = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.watchtower) params.watchtower = filters.watchtower;
      if (filters.severity) params.severity = filters.severity;
      if (filters.status) params.status = filters.status;
      const { data } = await adminApi.signals(params);
      setSignals(data?.signals || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (tab === "active") loadActive();
    if (tab === "heatmap") adminApi.signalHeatmap().then(({ data }) => setHeatmap(data?.heatmap || [])).catch(() => {});
    if (tab === "timeline") adminApi.signalTimeline({ hours: 24, limit: 200 }).then(({ data }) => setTimeline(data?.timeline || [])).catch(() => {});
    if (tab === "lifecycle") adminApi.signalLifecycle().then(({ data }) => setLifecycle(data?.lifecycle || {})).catch(() => {});
    if (tab === "correlations") adminApi.signalCorrelations(60).then(({ data }) => setCorrelations(data?.correlations || [])).catch(() => {});
    if (tab === "escalations") adminApi.signalEscalations(60).then(({ data }) => setEscalations(data?.escalations || [])).catch(() => {});
    if (tab === "watchlists") adminApi.scoresTop(20).then(({ data }) => setScoresTop(data?.scores || [])).catch(() => {});
    // eslint-disable-next-line
  }, [tab]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === "active") loadActive(); }, [filters]);

  const setStatus = async (id, status) => {
    setBusy(true);
    try {
      await adminApi.patchSignal(id, { status });
      await loadActive();
    } finally { setBusy(false); }
  };
  const escalate = async (id) => {
    setBusy(true);
    try {
      await adminApi.escalateSignal(id, { reason: "manual_from_admin" });
      await loadActive();
    } finally { setBusy(false); }
  };

  return (
    <div className="admin-page mesh-shell" data-testid="admin-signals">
      <div className="mesh-bg-radar" aria-hidden="true" />
      <div className="mesh-bg-particles" aria-hidden="true" />
      {/* Phase 26C — Mandated Compliance Lock™ banner */}
      <ComplianceLock variant="banner" testid="admin-signals-compliance" />
      <div className="admin-page__head">
        <h2 className="admin-page__title">Signal Command</h2>
        <div className="mesh-eyebrow">v2.0 · Global Intelligence Mesh</div>
      </div>

      <div className="mesh-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`mesh-tab ${tab === t.key ? "is-active" : ""}`}
            onClick={() => setTab(t.key)}
            data-testid={`signals-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "active" && (
        <section className="admin-section mesh-section">
          <div className="admin-filters" style={{ marginBottom: 10 }}>
            <select value={filters.watchtower} onChange={(e) => setFilters((f) => ({ ...f, watchtower: e.target.value }))} data-testid="signal-filter-wt">
              <option value="">All Watchtowers</option>
              {WATCHTOWERS.map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
            <select value={filters.severity} onChange={(e) => setFilters((f) => ({ ...f, severity: e.target.value }))} data-testid="signal-filter-sev">
              <option value="">All Severity</option>
              {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="signal-filter-status">
              <option value="">All Status</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button type="button" className="admin-linkbtn" onClick={loadActive} data-testid="signal-refresh">Refresh</button>
          </div>

          {loading ? <div className="admin-saved">Loading…</div> : (
            <div className="mesh-signal-grid">
              {signals.length === 0 && <div className="admin-foot-note">No signals match filters.</div>}
              {signals.map((s) => (
                <article key={s.id} className="mesh-signal-card" style={{ borderColor: s.severity_color || SEV_COLOR[s.severity] }} data-testid={`signal-card-${s.id}`}>
                  <header className="mesh-signal-card__head">
                    <span className="mesh-pill" style={{ borderColor: s.severity_color || SEV_COLOR[s.severity], color: s.severity_color || SEV_COLOR[s.severity] }}>{s.severity}</span>
                    <span className="mesh-pill mesh-pill--ghost">{s.watchtower}</span>
                    <span className="mesh-pill mesh-pill--ghost">{s.category}</span>
                    {s.asset && <span className="mesh-pill mesh-pill--gold">{s.asset}</span>}
                    <span className="mesh-score" title="Oracle Intelligence Score">
                      <span className="mesh-score__num">{s.oracle_score}</span>
                      <span className="mesh-score__lbl">SCORE</span>
                    </span>
                  </header>
                  <h3 className="mesh-signal-card__title">{s.title}</h3>
                  <p className="mesh-signal-card__sum">{s.summary}</p>
                  {s.sub_scores && (
                    <div className="mesh-subscores">
                      {Object.entries(s.sub_scores).map(([k, v]) => (
                        <span key={k} className="mesh-subscore" title={`${k}: ${v}`}>
                          <span className="mesh-subscore__k">{k.slice(0, 3).toUpperCase()}</span>
                          <span className="mesh-subscore__bar"><span style={{ width: `${Math.max(0, Math.min(100, v))}%` }} /></span>
                        </span>
                      ))}
                    </div>
                  )}
                  <footer className="mesh-signal-card__actions">
                    <span className="mesh-pill mesh-pill--ghost">STATUS: {s.status}</span>
                    <span className="mesh-pill mesh-pill--ghost">TIER: {s.tier_required || "free"}</span>
                    <div className="mesh-actions">
                      <button type="button" className="admin-linkbtn" disabled={busy || s.status === "MONITORING"} onClick={() => setStatus(s.id, "MONITORING")} data-testid={`signal-monitor-${s.id}`}>Monitor</button>
                      <button type="button" className="admin-linkbtn" disabled={busy || s.status === "CLOSED"} onClick={() => setStatus(s.id, "CLOSED")} data-testid={`signal-close-${s.id}`}>Close</button>
                      <button type="button" className="admin-linkbtn mesh-btn--danger" disabled={busy || s.escalated} onClick={() => escalate(s.id)} data-testid={`signal-escalate-${s.id}`}>Escalate</button>
                    </div>
                  </footer>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === "heatmap" && (
        <section className="admin-section mesh-section">
          <h3 className="admin-section__title">Signal Heatmap · Watchtower × Severity</h3>
          <div className="mesh-heatmap">
            <div className="mesh-heatmap__row mesh-heatmap__head">
              <span />{SEVERITIES.map((s) => <span key={s} style={{ color: SEV_COLOR[s] }}>{s}</span>)}
            </div>
            {heatmap.map((row) => (
              <div key={row.watchtower} className="mesh-heatmap__row" data-testid={`heatmap-row-${row.watchtower}`}>
                <span className="mesh-heatmap__label">{row.label}</span>
                {SEVERITIES.map((sev) => {
                  const n = row.counts?.[sev] || 0;
                  const intensity = Math.min(1, n / 6);
                  return (
                    <span key={sev} className="mesh-heatmap__cell" style={{ background: `${SEV_COLOR[sev]}${Math.round(intensity * 255).toString(16).padStart(2, "0")}` }}>
                      {n}
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "timeline" && (
        <section className="admin-section mesh-section">
          <h3 className="admin-section__title">24h Signal Timeline</h3>
          <ol className="mesh-timeline">
            {timeline.map((t) => (
              <li key={t.id} className="mesh-timeline__item" style={{ borderColor: SEV_COLOR[t.severity] }}>
                <span className="mesh-timeline__time">{new Date(t.created_at).toLocaleTimeString()}</span>
                <span className="mesh-pill" style={{ borderColor: SEV_COLOR[t.severity], color: SEV_COLOR[t.severity] }}>{t.severity}</span>
                <span className="mesh-pill mesh-pill--ghost">{t.watchtower}</span>
                {t.asset && <span className="mesh-pill mesh-pill--gold">{t.asset}</span>}
                <span className="mesh-timeline__title">{t.title}</span>
                <span className="mesh-score mesh-score--inline"><span className="mesh-score__num">{t.oracle_score}</span></span>
              </li>
            ))}
            {timeline.length === 0 && <div className="admin-foot-note">No signals in the last 24h.</div>}
          </ol>
        </section>
      )}

      {tab === "lifecycle" && (
        <section className="admin-section mesh-section">
          <h3 className="admin-section__title">Signal Lifecycle</h3>
          <div className="mesh-lifecycle">
            {STATUSES.map((s) => (
              <div key={s} className="mesh-lifecycle__cell" data-testid={`lifecycle-${s}`}>
                <div className="mesh-lifecycle__num">{lifecycle?.[s] ?? 0}</div>
                <div className="mesh-lifecycle__lbl">{s}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "correlations" && (
        <section className="admin-section mesh-section">
          <h3 className="admin-section__title">Signal Correlations</h3>
          <table className="admin-table" data-testid="correlations-table">
            <thead><tr><th>From</th><th>To</th><th>Strength</th><th>Basis</th><th>Created</th></tr></thead>
            <tbody>
              {correlations.map((c) => (
                <tr key={c.id}>
                  <td><code>{(c.from || "").slice(0, 8)}</code></td>
                  <td><code>{(c.to || "").slice(0, 8)}</code></td>
                  <td><strong>{c.strength}</strong></td>
                  <td>{Object.entries(c.basis || {}).map(([k, v]) => `${k}:${String(v)}`).join(" · ")}</td>
                  <td>{c.created_at}</td>
                </tr>
              ))}
              {correlations.length === 0 && <tr><td colSpan={5} className="admin-foot-note">No correlations yet.</td></tr>}
            </tbody>
          </table>
        </section>
      )}

      {tab === "escalations" && (
        <section className="admin-section mesh-section">
          <h3 className="admin-section__title">Escalations</h3>
          <ol className="mesh-timeline">
            {escalations.map((e) => (
              <li key={e.id} className="mesh-timeline__item" style={{ borderColor: SEV_COLOR[e.signal?.severity || "HIGH"] }}>
                <span className="mesh-timeline__time">{new Date(e.created_at).toLocaleTimeString()}</span>
                <span className="mesh-pill" style={{ borderColor: SEV_COLOR[e.signal?.severity || "HIGH"], color: SEV_COLOR[e.signal?.severity || "HIGH"] }}>{e.signal?.severity || "—"}</span>
                <span className="mesh-pill mesh-pill--ghost">{e.signal?.watchtower || "—"}</span>
                {e.signal?.asset && <span className="mesh-pill mesh-pill--gold">{e.signal.asset}</span>}
                <span className="mesh-timeline__title">{e.signal?.title || e.signal_id}</span>
                <span className="mesh-pill mesh-pill--ghost">{e.reason}</span>
                <span className="mesh-pill mesh-pill--ghost">by {e.actor}</span>
              </li>
            ))}
            {escalations.length === 0 && <div className="admin-foot-note">No escalations recorded.</div>}
          </ol>
        </section>
      )}

      {tab === "watchlists" && (
        <section className="admin-section mesh-section">
          <h3 className="admin-section__title">Top Oracle Intelligence Scores</h3>
          <div className="mesh-signal-grid">
            {scoresTop.map((s) => (
              <article key={s.id} className="mesh-signal-card" data-testid={`score-card-${s.asset}`}>
                <header className="mesh-signal-card__head">
                  <span className="mesh-pill mesh-pill--gold">{s.asset}</span>
                  <span className="mesh-pill mesh-pill--ghost">{s.watchtower}</span>
                  <span className="mesh-pill">{s.band}</span>
                  <span className="mesh-score"><span className="mesh-score__num">{s.composite}</span><span className="mesh-score__lbl">/100</span></span>
                </header>
                <p className="mesh-signal-card__sum">{s.note}</p>
                {s.sub_scores && (
                  <div className="mesh-subscores">
                    {Object.entries(s.sub_scores).map(([k, v]) => (
                      <span key={k} className="mesh-subscore"><span className="mesh-subscore__k">{k.slice(0,3).toUpperCase()}</span><span className="mesh-subscore__bar"><span style={{ width: `${v}%` }} /></span></span>
                    ))}
                  </div>
                )}
              </article>
            ))}
            {scoresTop.length === 0 && <div className="admin-foot-note">No scores yet.</div>}
          </div>
        </section>
      )}
    </div>
  );
}
